import { Controller } from "@hotwired/stimulus"

const ZONE_RANK = { hand: 0, graveyard: 1, exile: 2 }
const SORT_DIRECTIONS = { asc: 1, desc: -1 }

export function colorRequirementsMatch(requirements, enabledColors) {
  if (!Array.isArray(requirements) || requirements.length === 0) {
    return true
  }

  if (enabledColors.size === 0) {
    return true
  }

  return requirements.every((group) => {
    return Array.isArray(group) && group.some((color) => enabledColors.has(color))
  })
}

export function modeMatches(mode, state) {
  if (!state.enabledZones.has(mode.zone)) return false
  if (state.instantOnly && !mode.instant) return false
  if (!colorRequirementsMatch(mode.color_requirements, state.enabledColors)) return false

  const manaToPlay = Number(mode.mana_to_play)
  if (mode.x_spell) return manaToPlay <= state.mana
  if (state.mana === 8) return state.includeLowerMana || manaToPlay >= 8
  if (state.includeLowerMana) return manaToPlay <= state.mana
  return manaToPlay === state.mana
}

export function selectMode(modes, state) {
  return modes
    .filter((mode) => modeMatches(mode, state))
    .sort((a, b) => {
      return Number(a.mana_to_play) - Number(b.mana_to_play) ||
        Number(a.x_spell) - Number(b.x_spell) ||
        (ZONE_RANK[a.zone] ?? 9) - (ZONE_RANK[b.zone] ?? 9) ||
        Number(a.kind_rank) - Number(b.kind_rank) ||
        Number(a.emission_index) - Number(b.emission_index)
    })[0]
}

export default class extends Controller {
  static targets = ["card", "visibleCount", "totalCount", "manaButton", "colorButton", "zoneButton", "instantOnly", "includeLowerMana", "sortField", "sortDirection", "grid"]

  connect() {
    this.mana = 1
    this.enabledColors = new Set()
    this.enabledZones = new Set(["hand"])
    this.cards = this.cardTargets.map((element) => {
      return {
        element,
        name: element.dataset.name,
        rarityRank: Number(element.dataset.rarityRank || 99),
        modes: JSON.parse(element.dataset.modes || "[]")
      }
    })
    this.filter()
  }

  setMana(event) {
    this.mana = Number(event.currentTarget.dataset.mana)
    this.manaButtonTargets.forEach((button) => {
      const selected = Number(button.dataset.mana) === this.mana
      button.classList.toggle("active", selected)
      button.setAttribute("aria-checked", selected ? "true" : "false")
    })
    this.filter()
  }

  toggleColor(event) {
    this.toggleSetValue(this.enabledColors, event.currentTarget.dataset.color)
    this.syncPressed(this.colorButtonTargets, "color", this.enabledColors)
    this.filter()
  }

  toggleZone(event) {
    this.toggleSetValue(this.enabledZones, event.currentTarget.dataset.zone)
    this.syncPressed(this.zoneButtonTargets, "zone", this.enabledZones)
    this.filter()
  }

  filter() {
    const state = {
      mana: this.mana,
      enabledColors: this.enabledColors,
      enabledZones: this.enabledZones,
      instantOnly: this.hasInstantOnlyTarget && this.instantOnlyTarget.checked,
      includeLowerMana: this.hasIncludeLowerManaTarget && this.includeLowerManaTarget.checked
    }
    let visible = 0
    const visibleCards = []

    this.cards.forEach((card) => {
      const mode = selectMode(card.modes, state)
      card.element.hidden = !mode
      card.selectedMode = mode
      if (mode) {
        visible += 1
        visibleCards.push(card)
      }
    })

    this.sortCards(visibleCards)
    this.visibleCountTarget.textContent = String(visible)
    this.totalCountTarget.textContent = String(this.cards.length)
  }

  toggleSetValue(set, value) {
    if (set.has(value)) {
      set.delete(value)
    } else {
      set.add(value)
    }
  }

  syncPressed(buttons, dataKey, set) {
    buttons.forEach((button) => {
      const selected = set.has(button.dataset[dataKey])
      button.classList.toggle("active", selected)
      button.setAttribute("aria-pressed", selected ? "true" : "false")
    })
  }

  sortCards(visibleCards) {
    if (!this.hasGridTarget) return

    const field = this.hasSortFieldTarget ? this.sortFieldTarget.value : "rarity"
    const direction = SORT_DIRECTIONS[this.hasSortDirectionTarget ? this.sortDirectionTarget.value : "asc"] || 1

    visibleCards
      .sort((a, b) => direction * this.compareCards(a, b, field))
      .forEach((card) => this.gridTarget.appendChild(card.element))
  }

  compareCards(a, b, field) {
    if (field === "name") {
      return a.name.localeCompare(b.name)
    }

    if (field === "mana") {
      return Number(a.selectedMode?.mana_to_play ?? 99) - Number(b.selectedMode?.mana_to_play ?? 99) ||
        a.name.localeCompare(b.name)
    }

    return a.rarityRank - b.rarityRank ||
      Number(a.selectedMode?.mana_to_play ?? 99) - Number(b.selectedMode?.mana_to_play ?? 99) ||
      a.name.localeCompare(b.name)
  }
}
